import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./question.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { persistUserDataIds } from "../../functions/persistUserDataIds";

const CURRENT_LOBBY_KEY = "mythFactCurrentLobby";
const MAX_TEXT_LENGTH = 200;
/** Answer options: AI generation, review, edit, and add. */
const MAX_OPTION_LENGTH = 80;
const MIN_TOTAL_QUESTIONS = 2;
const MAX_TOTAL_QUESTIONS = 20;
const MIN_POINTS_PER_QUESTION = 1;
const MAX_POINTS_PER_QUESTION = 100;
const MAX_RETRIES = 3;

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") ||
      sessionStorage.getItem("userData") ||
      "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

function deriveNameFromEmailFallback(userData) {
  const direct = String(userData?.name ?? "").trim();
  if (direct && !/^null(\s+null)*$/i.test(direct)) return direct;
  const fn = String(userData?.firstName ?? userData?.first_name ?? "").trim();
  const ln = String(userData?.lastName ?? userData?.last_name ?? "").trim();
  const combined = [fn, ln].filter(Boolean).join(" ").trim();
  if (combined) return combined;
  const email = String(userData?.email ?? "").trim();
  if (email.includes("@")) return email.split("@")[0].trim();
  return "";
}

function resolveLobbyName(locationState) {
  const stateLobby =
    typeof locationState?.lobbyName === "string"
      ? locationState.lobbyName.trim()
      : "";
  if (stateLobby) return stateLobby;
  try {
    const ss = sessionStorage.getItem(CURRENT_LOBBY_KEY);
    if (ss && ss.trim()) return ss.trim();
  } catch {}
  return "";
}

function normalizeCorrectAnswer(value) {
  return String(value || "").trim();
}

function toAnswerId(value) {
  const n = Number(value);
  return n === 1 || n === 2 || n === 3 || n === 4 ? n : null;
}

function sanitizeCountInput(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function normalizeCountOnBlur(value, fallback = 1) {
  const raw = String(value ?? "").trim();
  if (!raw) return String(fallback);
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return String(fallback);
  return String(Math.trunc(n));
}

function clampQuestionCount(n) {
  const i = Math.trunc(Number(n));
  if (!Number.isFinite(i)) return MIN_TOTAL_QUESTIONS;
  return Math.min(MAX_TOTAL_QUESTIONS, Math.max(MIN_TOTAL_QUESTIONS, i));
}

function normalizeQuestionCountOnBlur(value, fallback = 10) {
  const raw = String(value ?? "").trim();
  if (!raw) return String(clampQuestionCount(fallback));
  return String(clampQuestionCount(raw));
}

function sanitizePointsInput(value) {
  return String(value ?? "").replace(/[^\d]/g, "");
}

function clampPointsPerQuestion(n) {
  const i = Math.trunc(Number(n));
  if (!Number.isFinite(i)) return MIN_POINTS_PER_QUESTION;
  return Math.min(MAX_POINTS_PER_QUESTION, Math.max(MIN_POINTS_PER_QUESTION, i));
}

function normalizePointsOnBlur(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return String(MIN_POINTS_PER_QUESTION);
  return String(clampPointsPerQuestion(raw));
}

function resizeTextarea(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function parseAiResponse(responseText) {
  const clean = String(responseText || "")
    .replace(/```json/gi, "")
    .replace(/```/g, "")
    .trim();

  if (!clean) throw new Error("AI returned empty response.");

  const tryParse = (candidate) => {
    try { return JSON.parse(candidate); } catch { return null; }
  };

  const direct = tryParse(clean);
  if (Array.isArray(direct)) return direct;
  if (Array.isArray(direct?.questions)) return direct.questions;

  const firstBracket = clean.indexOf("[");
  const lastBracket = clean.lastIndexOf("]");
  if (firstBracket !== -1 && lastBracket !== -1 && lastBracket > firstBracket) {
    const extracted = tryParse(clean.slice(firstBracket, lastBracket + 1));
    if (Array.isArray(extracted)) return extracted;
  }

  throw new Error("AI returned invalid format.");
}

function clampText(value, maxLength = MAX_TEXT_LENGTH) {
  return String(value || "").trim().slice(0, maxLength);
}

function toDisplayQuestion(raw) {
  const questionName = clampText(raw?.question_name);
  const optionOne    = clampText(raw?.option_one,   MAX_OPTION_LENGTH);
  const optionTwo    = clampText(raw?.option_two,   MAX_OPTION_LENGTH);
  const optionThree  = clampText(raw?.option_three, MAX_OPTION_LENGTH);
  const optionFour   = clampText(raw?.option_four,  MAX_OPTION_LENGTH);
  const correctAnswer = clampText(normalizeCorrectAnswer(raw?.correct_answer), MAX_OPTION_LENGTH);
  const answerId      = toAnswerId(raw?.answer_id);
  return {
    question_name:  questionName,
    option_one:     optionOne,
    option_two:     optionTwo,
    option_three:   optionThree,
    option_four:    optionFour,
    information:    "",
    option_one_popup:  "",
    option_two_popup:  "",
    correct_answer: correctAnswer,
    answer_id:      answerId,
    is_active:      "true",
    deleted:        false,
  };
}

/** 0-based index of the correct option; prefers answer_id, falls back to matching correct_answer text. */
function resolveCorrectOptionIndex(q) {
  const texts = [
    q.option_one,
    q.option_two,
    q.option_three,
    q.option_four,
  ];
  const id = toAnswerId(q.answer_id);
  if (id) {
    const idx = id - 1;
    if (normalizeCorrectAnswer(texts[idx]) === normalizeCorrectAnswer(q.correct_answer)) return idx;
  }
  const byText = texts.findIndex(
    (t) => normalizeCorrectAnswer(t) === normalizeCorrectAnswer(q.correct_answer)
  );
  return byText >= 0 ? byText : 0;
}

/** Randomly reorder A–D so the correct answer is not always option 1; keeps popups tied to their option text. */
function shuffleQuestionOptions(q) {
  const slots = [
    { text: q.option_one, popup: String(q.option_one_popup ?? "").trim() },
    { text: q.option_two, popup: String(q.option_two_popup ?? "").trim() },
    { text: q.option_three, popup: "" },
    { text: q.option_four, popup: "" },
  ];
  const order = [0, 1, 2, 3];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const correctOld = resolveCorrectOptionIndex(q);
  const correctText = slots[correctOld].text;
  const shuffled = order.map((oi) => slots[oi]);
  const newCorrectPos = order.indexOf(correctOld);
  return {
    ...q,
    option_one: clampText(shuffled[0].text, MAX_OPTION_LENGTH),
    option_one_popup: shuffled[0].popup,
    option_two: clampText(shuffled[1].text, MAX_OPTION_LENGTH),
    option_two_popup: shuffled[1].popup,
    option_three: clampText(shuffled[2].text, MAX_OPTION_LENGTH),
    option_four: clampText(shuffled[3].text, MAX_OPTION_LENGTH),
    correct_answer: clampText(normalizeCorrectAnswer(correctText), MAX_OPTION_LENGTH),
    answer_id: newCorrectPos + 1,
  };
}

function applyShuffleIfScoring(questions) {
  return questions.map((q) => shuffleQuestionOptions(q));
}

/** Tone / category from UI: every scenario and option must match this voice across the batch. */
const TONE_GUIDANCE = {
  "Safe at Work":
    "Tone: professional, inclusive, workplace-appropriate. Scenarios should feel like real judgment calls at work or in professional life (communication, ethics, boundaries, teamwork, clients). No graphic content, slurs, harassment, or NSFW themes.",
  Fun:
    "Tone: lighthearted and playful. Scenarios can be silly or surprising but stay PG, inclusive, and kind—wholesome humor is welcome. Avoid cruelty or punching down.",
  Wild:
    "Tone: bold, imaginative, high-energy hypotheticals. Push creative 'what if' choices while staying fictional and not a how-to for harm. No sexual content involving minors, hate, graphic gore, or detailed instructions for illegal acts.",
};

function toneBlockForCategory(category) {
  const key = String(category || "").trim();
  return TONE_GUIDANCE[key] || TONE_GUIDANCE.Fun;
}

function buildFourOptionPrompt({ topic, category, count, includeCorrect }) {
  const toneRules = toneBlockForCategory(category);
  const schemaLine = includeCorrect
    ? '{"question_name":"<string>","option_one":"<string max 80 chars>","option_two":"<string max 80 chars>","option_three":"<string max 80 chars>","option_four":"<string max 80 chars>","option_one_popup":"<≤200 chars or empty>","option_two_popup":"<≤200 chars or empty>","correct_answer":"<must equal exactly one option string>","answer_id":<1|2|3|4>}'
    : '{"question_name":"<string>","option_one":"<string max 80 chars>","option_two":"<string max 80 chars>","option_three":"<string max 80 chars>","option_four":"<string max 80 chars>","option_one_popup":"<≤200 chars or empty>","option_two_popup":"<≤200 chars or empty>"}';

  const scoringRules = includeCorrect
    ? `
Scoring fields (required on every object):
- correct_answer must exactly match one of option_one … option_four (same spelling and punctuation).
- answer_id: 1 = option_one, 2 = option_two, 3 = option_three, 4 = option_four.
- Pick the single best response for this tone: most constructive, professional, or context-appropriate—not the funniest wrong answer.`
    : "";

  return `You are writing for "What Would You Do?"—a scenario-based game where each item is a short dilemma and players choose one of four actions.

THEME (subject of the situations): "${topic}"
TONE / CATEGORY (use this voice consistently in EVERY question_name and ALL four options for ALL ${count} items): "${category}"
${toneRules}

For each object:
- question_name: Set up a concrete situation (brief context + tension), then end with a prompt like "What do you do?" (must end with ?).
- option_one through option_four: Four distinct, plausible responses that all sound like they belong to the same tone. No duplicate ideas; avoid throwaway gag options unless the tone is Fun and even then keep four real choices. Each option must be at most 80 characters.
- Do not include any separate "information" or explanation field beyond the schema below.
- option_one_popup and option_two_popup: optional short host notes (≤200 chars) or use empty strings "".

Every object must have exactly these fields:
${schemaLine}

Output: ONLY a JSON array of exactly ${count} objects. No markdown, no code fences, no text before or after the array.
Keep question_name at most 200 characters; each of option_one…option_four at most 80 characters.${scoringRules}

Rules:
- Every scenario and option must fit BOTH the theme "${topic}" AND the tone "${category}".
- Return ONLY the JSON array.`;
}

async function callAI(prompt, token) {
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (!backendBase) throw new Error("Backend URL is not configured.");
  if (!token) throw new Error("Auth token missing.");

  const resp = await fetch(`${backendBase}/generateAi`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ prompt }),
  });
  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(payload?.message || "Failed to connect to AI service.");
  return payload?.response;
}

async function fetchExactFourOptions({ topic, category, count, includeCorrect, token }) {
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const raw = await callAI(buildFourOptionPrompt({ topic, category, count, includeCorrect }), token);
    const parsed = parseAiResponse(raw);
    const normalized = parsed.map((q) => toDisplayQuestion(q)).filter((q) => q.question_name);
    const strict = normalized
      .filter((q) => q.option_one && q.option_two && q.option_three && q.option_four)
      .filter((q) => (includeCorrect ? Boolean(q.correct_answer) && Boolean(toAnswerId(q.answer_id)) : true));

    if (strict.length === count) {
      if (!includeCorrect) return strict.map((q) => ({ ...q, correct_answer: "", answer_id: null }));
      return applyShuffleIfScoring(
        strict.map((q) => ({ ...q, answer_id: toAnswerId(q.answer_id) }))
      );
    }

    if (strict.length > count) {
      const sliced = strict.slice(0, count);
      return includeCorrect
        ? applyShuffleIfScoring(
            sliced.map((q) => ({ ...q, answer_id: toAnswerId(q.answer_id) }))
          )
        : sliced.map((q) => ({ ...q, correct_answer: "", answer_id: null }));
    }
    if (attempt === MAX_RETRIES) {
      if (strict.length > 0) {
        return includeCorrect
          ? applyShuffleIfScoring(
              strict.map((q) => ({ ...q, answer_id: toAnswerId(q.answer_id) }))
            )
          : strict.map((q) => ({ ...q, correct_answer: "", answer_id: null }));
      }
      throw new Error(`Could not generate ${count} questions after ${MAX_RETRIES} attempts.`);
    }
  }
}

// ── Main component ─────────────────────────────────────────────────────────
function Question() {
  const dispatch    = useDispatch();
  const navigate    = useNavigate();
  const location    = useLocation();
  const adminToken  = useSelector(selectAdminToken);
  const { status, user } = useSelector((state) => state.auth);

  const [phase, setPhase]               = useState("create");
  const [topicChoice, setTopicChoice]   = useState("Safe at Work");
  const [topicCustom, setTopicCustom]   = useState("");
  const [questionCount, setQuestionCount] = useState("10");

  const [timerEnabled, setTimerEnabled]         = useState(false);
  const [questionPoint, setQuestionPoint]       = useState("5");
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isSettingsSaving, setIsSettingsSaving]   = useState(false);
  const [questions, setQuestions]               = useState([]);
  const [currentIndex, setCurrentIndex]         = useState(0);
  const [isGenerating, setIsGenerating]         = useState(false);
  const [isRegeneratingRemaining, setIsRegeneratingRemaining] = useState(false);
  const [isPublishing, setIsPublishing]         = useState(false);

  // mode: "view" | "edit" | "add"
  const [mode, setMode]           = useState("view");
  const [editingIdx, setEditingIdx] = useState(null);

  const [editQuestion,    setEditQuestion]    = useState("");
  const [editOptionOne,   setEditOptionOne]   = useState("");
  const [editOptionTwo,   setEditOptionTwo]   = useState("");
  const [editOptionThree, setEditOptionThree] = useState("");
  const [editOptionFour,  setEditOptionFour]  = useState("");
  const [editCorrectAnswer, setEditCorrectAnswer] = useState("");

  const [targetQuestionCount, setTargetQuestionCount] = useState(0);
  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const storedUserData = useMemo(() => readStoredUserData(), []);
  const backendBase    = useMemo(() => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""), []);
  const authPayload    = useMemo(() => {
    const userId         = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sessionId      = storedUserData.sessionId;
    const organizationId = storedUserData.organizationId;
    const lobby          = resolveLobbyName(location.state);
    return { userId, sessionId, organizationId, lobby };
  }, [storedUserData, location.state]);

  const topic    = useMemo(() => String(topicCustom || "").trim(), [topicCustom]);
  const category = useMemo(() => String(topicChoice || "").trim(), [topicChoice]);

  const totalRequested = Math.max(0, Number(questionCount) || 0);

  useEffect(() => {
    document.documentElement.classList.add("question-static-bg");
    document.body.classList.add("question-static-bg");
    return () => {
      document.documentElement.classList.remove("question-static-bg");
      document.body.classList.remove("question-static-bg");
    };
  }, []);

  useEffect(() => { dispatch(setBackButtonUrl("/lobby")); }, [dispatch, status, user]);

  const sendContentUpdate = async ({ timerStatus, timerValue, pointValue }) => {
    const { userId, sessionId, organizationId, lobby } = authPayload;
    if (!userId || !sessionId || !organizationId || !backendBase || !adminToken)
      throw new Error("Missing auth/session details.");
    const resp = await fetch(`${backendBase}/updateContentData`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
      body: JSON.stringify({
        userId, sessionId, organizationId, lobby,
        timer_status:    timerStatus ? "true" : "false",
        question_timer:  timerValue,
        question_point:  Number(pointValue) || 0,
      }),
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data?.success) throw new Error(data?.message || "Failed to update scoring/timer settings.");
    return data;
  };

  useEffect(() => {
    const { userId, sessionId, organizationId, lobby } = authPayload;
    if (!userId || !sessionId || !organizationId || !backendBase || !adminToken) return;

    let cancelled = false;
    setIsSettingsLoading(true);
    (async () => {
      try {
        const email     = String(storedUserData?.email ?? "").trim();
        const name      = deriveNameFromEmailFallback(storedUserData);
        const user_type = storedUserData.user_type || storedUserData.userType || storedUserData.role || storedUserData.source || "ORG_USER";
        const res = await fetch(`${backendBase}/addContent`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify({
            userId,
            sessionId,
            organizationId,
            lobby,
            email,
            name,
            user_type,
            gameId: storedUserData.gameId || "",
            token: storedUserData.token || "",
            reportId: storedUserData.reportId || storedUserData.reportid || "",
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to load scoring/timer settings.");
        persistUserDataIds({
          stageId: data.id,
          reportId: data.reportId,
          lobby,
        });
        const timerStatus = String(data.timer_status || "false").toLowerCase() === "true";
        setTimerEnabled(timerStatus);
        const points = Number(data.question_point);
        const loaded =
          Number.isFinite(points) && points > 0 ? points : 5;
        setQuestionPoint(String(clampPointsPerQuestion(loaded)));
      } catch (e) {
        if (!cancelled) setErrorMessage(String(e?.message || "Failed to load settings."));
      } finally {
        if (!cancelled) setIsSettingsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [adminToken, authPayload, backendBase]);

  const handleToggleTimer = async () => {
    if (isSettingsSaving || isSettingsLoading) return;
    const nextValue = !timerEnabled;
    setTimerEnabled(nextValue);
    setErrorMessage("");
    const normalizedPoints = String(
      clampPointsPerQuestion(Number(questionPoint) || MIN_POINTS_PER_QUESTION)
    );
    if (normalizedPoints !== String(questionPoint)) setQuestionPoint(normalizedPoints);
    setIsSettingsSaving(true);
    try {
      await sendContentUpdate({ timerStatus: nextValue, timerValue: "00:00", pointValue: nextValue ? normalizedPoints : 0 });
    } catch (e) {
      setTimerEnabled(!nextValue);
      setErrorMessage(String(e?.message || "Failed to update timer status."));
    } finally {
      setIsSettingsSaving(false);
    }
  };

  const persistSettingsIfValid = async () => {
    const normalizedPoints = String(
      clampPointsPerQuestion(Number(questionPoint) || MIN_POINTS_PER_QUESTION)
    );
    if (normalizedPoints !== String(questionPoint)) setQuestionPoint(normalizedPoints);
    await sendContentUpdate({ timerStatus: timerEnabled, timerValue: "00:00", pointValue: timerEnabled ? normalizedPoints : 0 });
  };

  useEffect(() => {
    const alive = questions.filter((q) => !q.deleted);
    if (!alive.length) { setCurrentIndex(0); return; }
    if (currentIndex > alive.length - 1) setCurrentIndex(alive.length - 1);
  }, [questions, currentIndex]);

  useEffect(() => {
    if (!errorMessage.includes("You can only keep up to")) return;
    const timer = window.setTimeout(() => {
      setErrorMessage((prev) => (prev.includes("You can only keep up to") ? "" : prev));
    }, 15000);
    return () => window.clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    if (phase !== "review" || (mode !== "edit" && mode !== "add")) return;
    document.querySelectorAll("textarea.q-line-input").forEach((el) => resizeTextarea(el));
  }, [
    phase,
    mode,
    editQuestion,
    editOptionOne,
    editOptionTwo,
    editOptionThree,
    editOptionFour,
  ]);

  const visibleQuestions = useMemo(
    () => questions.map((q, idx) => ({ ...q, _idx: idx })).filter((q) => !q.deleted),
    [questions]
  );
  const currentQuestion      = visibleQuestions[currentIndex] || null;
  const missingQuestionCount = Math.max(0, targetQuestionCount - visibleQuestions.length);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!topic.trim()) return setErrorMessage("Please enter a topic.");
    if (totalRequested <= 0) return setErrorMessage("Please enter number of questions.");
    if (totalRequested < MIN_TOTAL_QUESTIONS) {
      return setErrorMessage(`Enter at least ${MIN_TOTAL_QUESTIONS} questions (max ${MAX_TOTAL_QUESTIONS}).`);
    }
    if (totalRequested > MAX_TOTAL_QUESTIONS) return setErrorMessage(`Total questions cannot exceed ${MAX_TOTAL_QUESTIONS}.`);
    if (timerEnabled && !String(questionPoint).trim()) return setErrorMessage("Please enter points per question.");
    if (timerEnabled) {
      const pts = Number(questionPoint);
      if (
        !Number.isFinite(pts) ||
        pts < MIN_POINTS_PER_QUESTION ||
        pts > MAX_POINTS_PER_QUESTION
      ) {
        return setErrorMessage(
          `Points per question must be between ${MIN_POINTS_PER_QUESTION} and ${MAX_POINTS_PER_QUESTION}.`
        );
      }
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsGenerating(true);
    try {
      setIsSettingsSaving(true);
      await persistSettingsIfValid();
      const normalized = await fetchExactFourOptions({ topic, category, count: totalRequested, includeCorrect: timerEnabled, token: adminToken });
      if (!normalized?.length) throw new Error("No valid questions were generated.");
      setQuestions(normalized);
      setTargetQuestionCount(normalized.length);
      setCurrentIndex(0);
      setMode("view");
      setEditingIdx(null);
      setPhase("review");
    } catch (err) {
      setErrorMessage(err?.message || "Failed to generate questions.");
    } finally {
      setIsGenerating(false);
      setIsSettingsSaving(false);
    }
  };

  const handleRegenerateRemaining = async () => {
    if (!topic.trim()) { setErrorMessage("Topic missing. Please go back and generate again."); return; }
    if (missingQuestionCount <= 0) return;
    setErrorMessage("");
    setSuccessMessage("");
    setIsRegeneratingRemaining(true);
    try {
      const normalized = await fetchExactFourOptions({ topic, category, count: missingQuestionCount, includeCorrect: timerEnabled, token: adminToken });
      if (!normalized.length) throw new Error("No valid questions were generated.");
      setQuestions((prev) => [...prev, ...normalized]);
      if (!visibleQuestions.length) setCurrentIndex(0);
      setSuccessMessage(`Regenerated ${normalized.length} question(s).`);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to regenerate questions.");
    } finally {
      setIsRegeneratingRemaining(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    setCurrentIndex((p) => p - 1);
    setMode("view");
  };

  const handleNext = () => {
    if (currentIndex >= visibleQuestions.length - 1) return;
    setCurrentIndex((p) => p + 1);
    setMode("view");
  };

  const startEdit = (q) => {
    if (!q) return;
    setEditingIdx(q._idx);
    setEditQuestion(q.question_name);
    setEditOptionOne(clampText(q.option_one, MAX_OPTION_LENGTH));
    setEditOptionTwo(clampText(q.option_two, MAX_OPTION_LENGTH));
    setEditOptionThree(clampText(q.option_three, MAX_OPTION_LENGTH));
    setEditOptionFour(clampText(q.option_four, MAX_OPTION_LENGTH));
    setEditCorrectAnswer(clampText(q.correct_answer, MAX_OPTION_LENGTH));
    setMode("edit");
  };

  const startAdd = () => {
    if (visibleQuestions.length >= MAX_TOTAL_QUESTIONS) {
      setErrorMessage(`You can only keep up to ${MAX_TOTAL_QUESTIONS} total questions.`);
      return;
    }
    setErrorMessage("");
    setEditQuestion("");
    setEditOptionOne("");
    setEditOptionTwo("");
    setEditOptionThree("");
    setEditOptionFour("");
    setEditCorrectAnswer("");
    setEditingIdx(null);
    setMode("add");
  };

  const cancelEdit = () => {
    setMode("view");
    setEditingIdx(null);
  };

  const saveEdit = () => {
    const nextQ  = clampText(editQuestion);
    const nextO1 = clampText(editOptionOne, MAX_OPTION_LENGTH);
    const nextO2 = clampText(editOptionTwo, MAX_OPTION_LENGTH);
    const nextO3 = clampText(editOptionThree, MAX_OPTION_LENGTH);
    const nextO4 = clampText(editOptionFour, MAX_OPTION_LENGTH);
    const nextCorrect  = clampText(editCorrectAnswer, MAX_OPTION_LENGTH);
    const nextAnswerId = toAnswerId(
      nextCorrect === nextO1 ? 1 :
      nextCorrect === nextO2 ? 2 :
      nextCorrect === nextO3 ? 3 :
      nextCorrect === nextO4 ? 4 : null
    );

    if (!nextQ)  { setErrorMessage("Question cannot be empty."); return; }
    if (!nextO1 || !nextO2 || !nextO3 || !nextO4) { setErrorMessage("All 4 options are required."); return; }
    if (timerEnabled && (!nextCorrect || !nextAnswerId)) { setErrorMessage("Please set the correct answer (points ON)."); return; }
    setErrorMessage("");

    if (mode === "add") {
      if (visibleQuestions.length >= MAX_TOTAL_QUESTIONS) {
        setErrorMessage(`You can only keep up to ${MAX_TOTAL_QUESTIONS} total questions.`);
        return;
      }
      setQuestions((prev) => [
        ...prev,
        {
          question_name:   nextQ,
          option_one:      nextO1,
          option_two:      nextO2,
          option_three:    nextO3,
          option_four:     nextO4,
          information:     "",
          option_one_popup: "",
          option_two_popup: "",
          correct_answer:  timerEnabled ? nextCorrect : "",
          answer_id:       timerEnabled ? nextAnswerId : null,
          is_active:       "true",
          deleted:         false,
        },
      ]);
      setCurrentIndex(visibleQuestions.length);
      setMode("view");
      setEditingIdx(null);
      return;
    }

    // edit existing
    setQuestions((prev) =>
      prev.map((q, idx) =>
        idx === editingIdx
          ? {
              ...q,
              question_name:   nextQ,
              option_one:      nextO1,
              option_two:      nextO2,
              option_three:    nextO3,
              option_four:     nextO4,
              information:     "",
              option_one_popup: "",
              option_two_popup: "",
              correct_answer:  timerEnabled ? nextCorrect : "",
              answer_id:       timerEnabled ? nextAnswerId : null,
            }
          : q
      )
    );
    setMode("view");
    setEditingIdx(null);
  };

  const handleDelete = async (q) => {
    if (!q) return;
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this question?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    setQuestions((prev) => prev.map((row, idx) => idx === q._idx ? { ...row, deleted: true } : row));
    setMode("view");
    setEditingIdx(null);
  };

  const handlePublish = async () => {
    if (!backendBase || !adminToken) { setErrorMessage("Backend URL or token missing."); return; }
    const ready = visibleQuestions;
    if (!ready.length) { setErrorMessage("No questions to publish."); return; }

    setIsPublishing(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await persistSettingsIfValid();
      const { userId, sessionId, organizationId, lobby } = authPayload;
      const resp = await fetch(`${backendBase}/addAIQuestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId, sessionId, organizationId, lobby,
          question_point: timerEnabled ? (Number(questionPoint) || 0) : 0,
          point_status:   timerEnabled ? "true" : "false",
          question_category: category,
          questions: ready.map((q) => ({
            question_name:   q.question_name,
            option_one:      q.option_one,
            option_two:      q.option_two,
            option_three:    q.option_three,
            option_four:     q.option_four,
            information:     "",
            option_one_popup: q.option_one_popup || "",
            option_two_popup: q.option_two_popup || "",
            answer_id:       timerEnabled ? q.answer_id : null,
            correct_answer:  timerEnabled ? q.correct_answer : null,
            point_status:    timerEnabled ? "true" : "false",
            question_point:  timerEnabled ? (Number(questionPoint) || 0) : 0,
            question_category: category,
            is_active:       "true",
          })),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data?.success) throw new Error(data?.message || "Failed to publish questions.");

      setSuccessMessage(`Published ${data.inserted ?? ready.length} questions successfully.`);
      const randomPin  = Math.floor(1000 + Math.random() * 9000);
      const lobbyLabel = authPayload.lobby || "main";
      const shareBase  = String(process.env.REACT_APP_BASE_URL || window.location.origin || "").replace(/\/+$/, "");
      const joinLink   = `${shareBase}/game?pin=${randomPin}&lobby=${encodeURIComponent(lobbyLabel)}`;
      navigate("/host", { replace: true, state: { pin: randomPin, link: joinLink, lobbyName: lobbyLabel } });

      setPhase("create");
      setTopicChoice("Safe at Work");
      setTopicCustom("");
      setQuestionCount("10");
      setQuestions([]);
      setTargetQuestionCount(0);
      setCurrentIndex(0);
      setMode("view");
      setEditingIdx(null);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to publish questions.");
    } finally {
      setIsPublishing(false);
    }
  };

  // ── helpers for rendering ─────────────────────────────────────────────────
  const isOnLastQuestion = currentIndex >= visibleQuestions.length - 1;
  const canPublish       = missingQuestionCount <= 0 && isOnLastQuestion;
  const setupLocked =
    isGenerating || isSettingsLoading || isSettingsSaving;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="question-page">
      <div className="question-stack">

        {/* ════════════════ CREATE PHASE ════════════════ */}
        {phase === "create" ? (
          <div className="q-setup">
            <div className="q-setup-row">
              {/* Topic card */}
              <div className="q-setup-card q-setup-card--topic">
                <input
                  className={`q-setup-card-header-input ${topicCustom.trim() ? "is-active" : ""}`}
                  type="text"
                  value={topicCustom}
                  onChange={(e) => { setTopicCustom(e.target.value); setErrorMessage(""); }}
                  placeholder="TOPIC"
                  disabled={setupLocked}
                  maxLength={MAX_TEXT_LENGTH}
                  autoComplete="off"
                  aria-label="Topic name"
                />
                <div className="q-setup-card-body">
                  <div className="q-topic-options" role="radiogroup" aria-label="Topic tone">
                    {["Safe at Work", "Fun", "Wild"].map((label) => (
                      <button
                        key={label}
                        type="button"
                        className={`q-topic-pill ${topicChoice === label ? "is-active" : ""}`}
                        onClick={() => { setTopicChoice(label); setErrorMessage(""); }}
                        disabled={setupLocked}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="q-setup-inline">
                    <div className="q-setup-inline-label">NO. OF QUESTIONS</div>
                    <input
                      className="q-setup-inline-input"
                      type="text"
                      inputMode="numeric"
                      value={questionCount}
                      onChange={(e) => {
                        const raw = sanitizeCountInput(e.target.value);
                        if (!raw) { setQuestionCount(""); setErrorMessage(""); return; }
                        const parsed = Math.trunc(Number(raw) || 0);
                        setQuestionCount(String(Math.min(Math.max(0, parsed), MAX_TOTAL_QUESTIONS)));
                        setErrorMessage("");
                      }}
                      onBlur={() => setQuestionCount((prev) => normalizeQuestionCountOnBlur(prev, 10))}
                      disabled={setupLocked}
                      aria-label="Number of questions"
                    />
                  </div>
                </div>
              </div>

              {/* Scoring card */}
              <div className="q-setup-card q-setup-card--score">
                <div className="q-setup-card-header">SCORING SYSTEM</div>
                <div className="q-setup-card-body">
                  <div
                    className={`q-seg-toggle ${timerEnabled ? "q-seg-toggle--on" : "q-seg-toggle--off"}`}
                    role="group"
                    aria-label="Scoring system"
                  >
                    <button
                      type="button"
                      className={`q-seg-toggle-btn ${timerEnabled ? "is-selected" : ""}`}
                      onClick={() => { if (!timerEnabled) void handleToggleTimer(); }}
                      disabled={setupLocked || timerEnabled}
                    >
                      ON
                    </button>
                    <button
                      type="button"
                      className={`q-seg-toggle-btn ${!timerEnabled ? "is-selected" : ""}`}
                      onClick={() => { if (timerEnabled) void handleToggleTimer(); }}
                      disabled={setupLocked || !timerEnabled}
                    >
                      OFF
                    </button>
                  </div>
                  {timerEnabled ? (
                    <div className="q-setup-inline">
                      <div className="q-setup-inline-label">POINTS PER QUESTION</div>
                      <input
                        className="q-setup-inline-input"
                        type="text"
                        inputMode="numeric"
                        value={questionPoint}
                        onChange={(e) => {
                          const raw = sanitizePointsInput(e.target.value);
                          if (!raw) {
                            setQuestionPoint("");
                            setErrorMessage("");
                            return;
                          }
                          const n = Math.trunc(Number(raw) || 0);
                          setQuestionPoint(String(Math.min(Math.max(0, n), MAX_POINTS_PER_QUESTION)));
                          setErrorMessage("");
                        }}
                        onBlur={() => setQuestionPoint((prev) => normalizePointsOnBlur(prev))}
                        disabled={setupLocked}
                        aria-label="Points per question"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <button
              type="button"
              className="q-setup-generate"
              onClick={handleGenerate}
              disabled={setupLocked}
            >
              {isGenerating ? "Generating..." : "GENERATE"}
            </button>
          </div>

        ) : (
          /* ════════════════ REVIEW PHASE ════════════════ */
          <div className="q-preview-panel">

            {/* ── EMPTY STATE (all questions deleted) ── */}
            {visibleQuestions.length === 0 && mode !== "add" ? (
              <div className="q-empty-state">
                <p className="q-empty-state-title">No questions available</p>
                <p className="q-empty-state-text">
                  All questions were removed. Add a new question or go back to generate more.
                </p>
                <div className="q-empty-state-actions">
                  <button type="button" className="q-footer-pill" onClick={startAdd}>
                    ADD QUESTION
                  </button>
                  <button
                    type="button"
                    className="q-footer-pill primary"
                    onClick={() => {
                      setPhase("create");
                      setMode("view");
                      setEditingIdx(null);
                      setErrorMessage("");
                      setSuccessMessage("");
                    }}
                  >
                    BACK TO CREATE
                  </button>
                </div>
              </div>
            ) : null}

            {/* ── VIEW MODE (Image 1) ── */}
            {mode === "view" && currentQuestion && (
              <div className="q-preview-card">
                {/* Question + EDIT / DELETE */}
                <div className="q-preview-top">
                  <div className="q-preview-question-pill">
                    {currentQuestion.question_name || "QUESTION"}
                  </div>
                  <div className="q-preview-action-btns">
                    <button
                      type="button"
                      className="q-action-pill"
                      onClick={() => startEdit(currentQuestion)}
                    >
                      EDIT
                    </button>
                    <button
                      type="button"
                      className="q-action-pill"
                      onClick={() => handleDelete(currentQuestion)}
                    >
                      DELETE
                    </button>
                  </div>
                </div>

                {/* Options list */}
                <div className="q-preview-options-list">
                  {[
                    { text: currentQuestion.option_one,   id: 1 },
                    { text: currentQuestion.option_two,   id: 2 },
                    { text: currentQuestion.option_three, id: 3 },
                    { text: currentQuestion.option_four,  id: 4 },
                  ].map(({ text, id }) => {
                    const isCorrect = timerEnabled && toAnswerId(currentQuestion.answer_id) === id;
                    return (
                      <div
                        key={id}
                        className={`q-preview-opt-text ${isCorrect ? "is-correct" : ""}`}
                      >
                        {text}
                      </div>
                    );
                  })}
                </div>

                {/* Footer: PREV / NEXT / ADD QUESTION / SAVE */}
                <div className="q-preview-footer">
                  {missingQuestionCount > 0 ? (
                    <button
                      type="button"
                      className="q-footer-pill"
                      onClick={handleRegenerateRemaining}
                      disabled={isRegeneratingRemaining}
                    >
                      {isRegeneratingRemaining ? "REGENERATING..." : "REGENERATE REMAINING"}
                    </button>
                  ) : null}

                  <button
                    type="button"
                    className="q-footer-pill"
                    onClick={handlePrev}
                    disabled={currentIndex <= 0}
                  >
                    PREV
                  </button>

                  {!isOnLastQuestion && (
                    <button
                      type="button"
                      className="q-footer-pill primary"
                      onClick={handleNext}
                    >
                      NEXT
                    </button>
                  )}

                  <button
                    type="button"
                    className="q-footer-pill"
                    onClick={startAdd}
                    disabled={visibleQuestions.length >= MAX_TOTAL_QUESTIONS}
                  >
                    ADD QUESTION
                  </button>

                  {canPublish && (
                    <button
                      type="button"
                      className="q-footer-pill save"
                      onClick={handlePublish}
                      disabled={isPublishing}
                    >
                      {isPublishing ? "SAVING..." : "SAVE"}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── EDIT MODE (Image 2) ── */}
            {mode === "edit" && (
              <div className="q-edit-card">
                {/* SAVE / CANCEL float top-right */}
                <div className="q-edit-header-actions">
                  <button type="button" className="q-action-pill" onClick={saveEdit}>SAVE</button>
                  <button type="button" className="q-action-pill" onClick={cancelEdit}>CANCEL</button>
                </div>

                {/* Stacked input fields */}
                <div className="q-edit-fields">
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editQuestion}
                    onChange={(e) => setEditQuestion(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="What's your question?"
                    maxLength={MAX_TEXT_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionOne}
                    onChange={(e) => setEditOptionOne(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 1"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionTwo}
                    onChange={(e) => setEditOptionTwo(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 2"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionThree}
                    onChange={(e) => setEditOptionThree(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 3"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionFour}
                    onChange={(e) => setEditOptionFour(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 4"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  {timerEnabled && (
                    <select
                      className="q-line-input"
                      value={editCorrectAnswer}
                      onChange={(e) => setEditCorrectAnswer(e.target.value)}
                    >
                      <option value="">Correct answer</option>
                      <option value={editOptionOne   || ""}>{editOptionOne   || "Option 1"}</option>
                      <option value={editOptionTwo   || ""}>{editOptionTwo   || "Option 2"}</option>
                      <option value={editOptionThree || ""}>{editOptionThree || "Option 3"}</option>
                      <option value={editOptionFour  || ""}>{editOptionFour  || "Option 4"}</option>
                    </select>
                  )}
                </div>
              </div>
            )}

            {/* ── ADD MODE (Image 3) ── */}
            {mode === "add" && (
              <div className="q-add-layout">
                {/* LEFT: frozen view of current question */}
                <div className="q-add-left">
                  {currentQuestion ? (
                    <>
                      <div className="q-preview-question-pill">
                        {currentQuestion.question_name || "QUESTION"}
                      </div>
                      <div className="q-preview-options-list">
                        {[
                          { text: currentQuestion.option_one,   id: 1 },
                          { text: currentQuestion.option_two,   id: 2 },
                          { text: currentQuestion.option_three, id: 3 },
                          { text: currentQuestion.option_four,  id: 4 },
                        ].map(({ text, id }) => {
                          const isCorrect = timerEnabled && toAnswerId(currentQuestion.answer_id) === id;
                          return (
                            <div key={id} className={`q-preview-opt-text ${isCorrect ? "is-correct" : ""}`}>
                              {text}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="q-preview-question-pill" style={{ opacity: 0.5 }}>
                      No current question
                    </div>
                  )}
                </div>

                {/* RIGHT: new question form */}
                <div className="q-add-right">
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editQuestion}
                    onChange={(e) => setEditQuestion(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Question"
                    maxLength={MAX_TEXT_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionOne}
                    onChange={(e) => setEditOptionOne(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 1"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionTwo}
                    onChange={(e) => setEditOptionTwo(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 2"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionThree}
                    onChange={(e) => setEditOptionThree(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 3"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  <textarea
                    className="q-line-input"
                    rows={1}
                    value={editOptionFour}
                    onChange={(e) => setEditOptionFour(e.target.value)}
                    onInput={(e) => resizeTextarea(e.currentTarget)}
                    placeholder="Option 4"
                    maxLength={MAX_OPTION_LENGTH}
                  />
                  {timerEnabled && (
                    <select
                      className="q-line-input"
                      value={editCorrectAnswer}
                      onChange={(e) => setEditCorrectAnswer(e.target.value)}
                    >
                      <option value="">Correct answer</option>
                      <option value={editOptionOne   || ""}>{editOptionOne   || "Option 1"}</option>
                      <option value={editOptionTwo   || ""}>{editOptionTwo   || "Option 2"}</option>
                      <option value={editOptionThree || ""}>{editOptionThree || "Option 3"}</option>
                      <option value={editOptionFour  || ""}>{editOptionFour  || "Option 4"}</option>
                    </select>
                  )}

                  <div className="q-add-right-actions">
                    <button type="button" className="q-action-pill" onClick={saveEdit}>SAVE</button>
                    <button type="button" className="q-action-pill" onClick={cancelEdit}>CANCEL</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {errorMessage   ? <p className="q-error">{errorMessage}</p>     : null}
        {successMessage ? <p className="q-success">{successMessage}</p> : null}
      </div>
    </div>
  );
}

export default Question;
